import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, MessageCircle, Phone, X } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV_LINKS, SITE } from "@/constants/site";
import { scrollToSection, useLenis } from "@/components/LenisProvider";

export default function Header() {
  const lenis = useLenis();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(lenis, href);
  };

  return (
    <header
      className="fixed inset-x-0 top-0 z-50"
      data-testid="site-header"
    >
      <div
        className={`border-b bg-cream/70 backdrop-blur-xl transition-shadow duration-300 ${
          scrolled ? "border-cream-border shadow-[0_8px_30px_rgba(13,21,38,0.06)]" : "border-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
          <a
            href="#inicio"
            onClick={(e) => go(e, "#inicio")}
            data-testid="header-logo-link"
            aria-label={SITE.fullName}
          >
            <Logo />
          </a>

          <nav className="hidden items-center gap-9 lg:flex">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`header-nav-${link.label.toLowerCase()}`}
                onClick={(e) => go(e, link.href)}
                className="group relative text-sm font-medium tracking-wide text-olive-mid transition-colors duration-300 hover:text-olive"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-terracotta transition-transform duration-300 group-hover:scale-x-100" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={SITE.phoneHref}
              data-testid="header-phone-button"
              className="hidden items-center gap-2 rounded-full bg-terracotta px-6 py-3 text-sm font-medium text-cream transition-colors duration-300 hover:bg-terracotta-dark sm:inline-flex"
            >
              <Phone className="h-4 w-4" />
              Llámanos
            </a>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid="header-mobile-menu-toggle"
              aria-label="Abrir menú"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-olive/15 text-olive lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="border-b border-cream-border bg-cream/95 px-6 pb-8 pt-4 backdrop-blur-xl lg:hidden"
            data-testid="header-mobile-menu"
          >
            <div className="flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  onClick={(e) => go(e, link.href)}
                  className="rounded-xl px-4 py-3 font-serif text-2xl text-olive transition-colors duration-200 hover:bg-cream-soft"
                >
                  {link.label}
                </a>
              ))}
              <a
                href={SITE.phoneHref}
                data-testid="mobile-nav-phone-button"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-6 py-3.5 text-sm font-medium text-cream"
              >
                <Phone className="h-4 w-4" />
                Llámanos: {SITE.phoneDisplay}
              </a>
              <a
                href={SITE.whatsappHref}
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-nav-whatsapp-button"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-olive/15 px-6 py-3.5 text-sm font-medium text-olive"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
